import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  ArrowsClockwise,
  CheckCircle,
  ReadCvLogo,
  X,
  type Icon,
} from "@phosphor-icons/react";
import { ChecklistItem } from "hooks/api/documentGeneration/useGetDocumentChecklist";
import { buildClinicianNavUrl, buildSectionNavUrl } from "./preflightSectionRoutes";
import OverrideChip from "./OverrideChip";

interface GroupedCategory {
  category: string;
  items: ChecklistItem[];
}

interface PreflightCheckStepProps {
  /** Display title of the document type */
  documentTitle: string;
  /** Phosphor icon component for the document type */
  documentIcon?: Icon;
  /** Client ID — used to build the "Go To" navigation URL */
  clientId: string;
  /** Primary clinician (BCBA) id — used for sections that route to the clinician profile */
  primaryClinicianId?: string | number;
  /** Logged-in clinician id — used for sections that route to the document author's profile */
  currentClinicianId?: string | number;
  /** Authenticated user's clinician id — used to select the self-service profile route */
  userClinicianId?: string | number;
  /** Full checklist (both present and missing items). */
  checklist: ChecklistItem[];
  /** Whether the checklist query is fetching — the initial-missing snapshot is frozen only once this settles. */
  isFetching: boolean;
  /** Whether an active override waives this document's readiness checks */
  isOverridden?: boolean;
  /** Label for the advance button, naming the step it leads to */
  continueLabel?: string;
  /** Callback to advance past the unmet checks — only offered while overridden */
  onContinue: () => void;
  /** Callback to return to document type selection */
  onBack: () => void;
  /** Callback to close the dialog */
  onClose: () => void;
  /** Callback to refetch the checklist */
  onCheckAgain: () => void;
}

/**
 * Pre-flight check step of the Generate Document dialog.
 * Shown when the selected document type has missing required data.
 * Displays missing items grouped by category with links to resolve them.
 *
 * @param props
 * @param props.documentTitle - Display title of the document type
 * @param props.documentIcon - Phosphor icon component for the document type
 * @param props.checklist - Full checklist items array
 * @param props.isOverridden - Whether an active override waives these checks
 * @param props.continueLabel - Label for the advance button, naming its destination
 * @param props.onContinue - Callback to advance past the unmet checks
 * @param props.onBack - Callback to return to document type selection
 * @param props.onClose - Callback to close the dialog
 * @param props.onCheckAgain - Callback to refetch the checklist
 */
const PreflightCheckStep: React.FC<PreflightCheckStepProps> = ({
  documentTitle,
  documentIcon: DocumentIcon = ReadCvLogo,
  clientId,
  primaryClinicianId,
  currentClinicianId,
  userClinicianId,
  checklist,
  isFetching,
  isOverridden = false,
  continueLabel,
  onContinue,
  onBack,
  onClose,
  onCheckAgain,
}) => {
  // Freeze the initially-missing item names once, from settled (non-fetching) data, so the
  // resolved/total counts stay stable as items are fixed and re-checked. Waiting on !isFetching
  // avoids snapshotting stale cached data that react-query serves while refetching in the background.
  const [initialMissingNames, setInitialMissingNames] = useState<string[] | null>(null);
  useEffect(() => {
    if (initialMissingNames === null && !isFetching && checklist.length > 0) {
      setInitialMissingNames(checklist.filter((item) => !item.isPresent).map((item) => item.name));
    }
  }, [initialMissingNames, isFetching, checklist]);

  if (initialMissingNames === null) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 11 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Always show the initial missing items, but with their current isPresent status
  const trackedItems = checklist.filter((item) => initialMissingNames.includes(item.name));

  const totalCount = initialMissingNames.length;
  const resolvedCount = trackedItems.filter((item) => item.isPresent).length;
  const progressValue = totalCount > 0 ? (resolvedCount / totalCount) * 100 : 0;

  const grouped = trackedItems.reduce<Record<string, ChecklistItem[]>>((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {});
  const groupedCategories: GroupedCategory[] = Object.entries(grouped).map(([category, items]) => ({
    category,
    items,
  }));

  // Per-item Go To overrides — these share a `section` with other doc types but need a
  // different destination (e.g. logged-in clinician's profile vs. client's primary BCBA).
  const HAR_LOGGED_IN_PROFILE_FIELDS = ["clinician_name", "clinician_email"];
  const SUPERVISOR_FIELDS = [
    "supervisor_first_name",
    "supervisor_last_name",
    "supervisor_credentials",
  ];

  const urlForItem = (item: ChecklistItem) => {
    if (SUPERVISOR_FIELDS.includes(item.name)) return "/clinicians";
    if (HAR_LOGGED_IN_PROFILE_FIELDS.includes(item.name)) {
      return buildClinicianNavUrl(currentClinicianId, "profile", userClinicianId);
    }
    return buildSectionNavUrl(item.section, {
      clientId,
      primaryClinicianId,
      currentClinicianId,
      userClinicianId,
    });
  };

  const handleGoToField = (item: ChecklistItem) => {
    const url = urlForItem(item);
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 7,
          py: 5,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <IconButton
            onClick={onBack}
            aria-label="Back to document selection"
            sx={{
              border: "1px solid",
              borderColor: "grey.300",
              borderRadius: "10px",
              boxShadow: 1,
              p: "5px",
            }}
          >
            <ArrowLeft size={20} />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h2">
              Pre-flight check
            </Typography>
            <Typography variant="body2" color="text.secondary">
              A few things are needed before we can generate this document
            </Typography>
          </Box>
        </Stack>
        <IconButton onClick={onClose} aria-label="Close dialog" size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 7, pb: 7, pt: 0 }}>
        <Stack spacing={5} sx={{ mt: 7 }}>
          {isOverridden && <OverrideChip />}

          {/* Document type label */}
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ color: "text.secondary", display: "flex" }}>
              <DocumentIcon size={20} weight="duotone" />
            </Box>
            <Typography variant="body1" color="text.secondary">
              Document:{" "}
              <Typography component="span" variant="body1" color="text.primary">
                {documentTitle}
              </Typography>
            </Typography>
          </Stack>

          {/* Progress bar */}
          <Stack direction="row" alignItems="center" spacing={3}>
            <LinearProgress
              variant="determinate"
              value={progressValue}
              color="primary"
              sx={{
                flex: 1,
                height: 8,
                borderRadius: "100px",
                backgroundColor: "purple.200",
              }}
            />
            <Typography variant="body2" color="text.secondary" whiteSpace="nowrap">
              {resolvedCount} of {totalCount} resolved
            </Typography>
          </Stack>

          {/* Category accordions */}
          <Stack sx={{ gap: 5 }}>
            {groupedCategories.map(({ category, items }, index) => (
              <Accordion
                key={category}
                defaultExpanded={groupedCategories.length <= 2 || index === 0}
                disableGutters
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  overflow: "hidden",
                  "&:before": { display: "none" },
                  "&.Mui-expanded": { m: 0 },
                  "&, &:first-of-type, &:last-of-type": {
                    borderRadius: "12px",
                  },
                }}
              >
                <AccordionSummary>
                  <Stack direction="row" alignItems="center" spacing={3}>
                    <Typography variant="subtitle1">{category}</Typography>
                    <Chip
                      label={`${items.filter((i) => !i.isPresent).length} item${items.filter((i) => !i.isPresent).length === 1 ? "" : "s"}`}
                      size="small"
                      variant="outlined"
                      color="warning"
                    />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Stack>
                    {items.map((item) => (
                      <Stack
                        key={item.name}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                          px: 4,
                          py: 3,
                          borderRadius: "8px",
                          gap: 3,
                          "&:hover": {
                            bgcolor: "action.hover",
                          },
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body1">{item.label}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.description}
                          </Typography>
                        </Box>
                        {item.isPresent ? (
                          <Box sx={{ color: "success.main", display: "flex", flexShrink: 0 }}>
                            <CheckCircle size={24} weight="fill" />
                          </Box>
                        ) : (
                          <Button
                            size="small"
                            variant="contained"
                            color="secondary"
                            endIcon={<ArrowUpRight size={16} />}
                            onClick={() => handleGoToField(item)}
                            disabled={!urlForItem(item)}
                            sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                          >
                            Go To {item.section}
                          </Button>
                        )}
                      </Stack>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 7, pt: 5, pb: 5, gap: 2 }}>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<ArrowsClockwise size={20} />}
          onClick={onCheckAgain}
        >
          Check Again
        </Button>
        {isOverridden && (
          <Button
            variant="contained"
            color="primary"
            endIcon={<ArrowRight size={16} />}
            onClick={onContinue}
          >
            {continueLabel}
          </Button>
        )}
      </DialogActions>
    </>
  );
};

export default PreflightCheckStep;
